import React from 'react';
import Select, { type SelectProps } from '@amzn/awsui-components-core/select';
import type { StarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import { PNL_SOURCE_ID } from 'src/components/star-grid/datasets/datasetConstants';
import styles from 'src/components/star-grid/datasets/components/styles/DataSourcePicker.module.scss';

export interface DataSourceOption {
  readonly id: string;
  readonly name: string;
  readonly version?: number;
}

interface Props {
  readonly text: StarGridText;
  readonly datasets: readonly DataSourceOption[];
  /** `pnl` or a dataset id. */
  readonly value: string;
  readonly onSelectPnl: () => void;
  readonly onSelectDataset: (id: string) => void;
  /** Dataset list still in flight: the dropdown reports it instead of looking empty. */
  readonly loading?: boolean;
  readonly loadError?: string | null;
  readonly disabled?: boolean;
}

const datasetLabel = (dataset: DataSourceOption): string => (dataset.version ? `${dataset.name} v${dataset.version}` : dataset.name);

/** The Report Details data source picker: P&L (default) or a published global dataset. Onboarding lives under Admin. */
export const DataSourcePicker: React.FC<Props> = ({ text, datasets, value, onSelectPnl, onSelectDataset, loading, loadError, disabled }) => {
  const options: SelectProps.Option[] = [
    { value: PNL_SOURCE_ID, label: text('dataSourcePnl') },
    ...datasets.map((dataset) => ({ value: dataset.id, label: datasetLabel(dataset) }))
  ];
  const selected = options.find((option) => option.value === value) ?? options[0];

  const handleChange = (chosen: string | undefined): void => {
    if (chosen === PNL_SOURCE_ID || chosen === undefined) {
      onSelectPnl();
    } else {
      onSelectDataset(chosen);
    }
  };

  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>{text('dataSourceLabel')}</span>
      <div className={styles.row}>
        <div className={styles.selectBox} title={typeof selected.label === 'string' ? selected.label : undefined}>
          <Select
            ariaLabel={text('dataSourceLabel')}
            data-testid="data-source-picker"
            disabled={disabled}
            options={options}
            selectedOption={selected}
            statusType={loading ? 'loading' : loadError ? 'error' : 'finished'}
            loadingText={text('loadingDatasets')}
            errorText={text('dataSourceLoadError')}
            expandToViewport
            onChange={({ detail }) => handleChange(detail.selectedOption.value)}
          />
        </div>
      </div>
    </div>
  );
};
